import AddIcon from "@mui/icons-material/Add"
import DeleteIcon from "@mui/icons-material/Delete"
import {
    Alert,
    Box,
    Button,
    Chip,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material"

import {
    getStageTypeChipSx,
    roleChipColor,
    stageActionLabels,
    stageTypeChipIcons,
    stageTypeLabels,
} from "../../DocumentWorkflowConfig"
import {
    formatDateOnly,
    isPendingWorkflowActionStatus,
    mapWorkflowActionStatus,
} from "../../documentWorkflowHelpers"
import type {
    UiStageType,
    WorkflowMemberViewModel,
} from "../../documentWorkflowTypes"
import { LpField } from "../../../../../app/components/LpField"
import { MemberIdentity } from "../../../../../app/components/MemberIdentity"

export type WorkflowStageMembersCardProps = {
    stageId: number
    order: number
    title: string
    description: string
    stageType: UiStageType
    isActiveStage: boolean
    members: WorkflowMemberViewModel[]
    onAddMemberClick: (stageId: number) => void
    onRemoveMemberClick: (
        stageId: number,
        stageTitle: string,
        member: WorkflowMemberViewModel
    ) => void
}

export const WorkflowStageMembersCard = ({
    stageId,
    order,
    title,
    description,
    stageType,
    isActiveStage,
    members,
    onAddMemberClick,
    onRemoveMemberClick,
}: WorkflowStageMembersCardProps) => {
    const addButtonColor = stageType === "EDIT" ? "info" : "secondary"

    return (
        <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ mb: 2, overflow: "hidden" }}
        >
            <Table
                size="small"
                sx={{ tableLayout: "fixed", width: "100%" }}
            >
                <colgroup>
                    <col style={{ width: 50 }} />
                    <col />
                    <col style={{ width: 360 }} />
                    <col style={{ width: 88 }} />
                </colgroup>

                <TableHead>
                    <TableRow>
                        <TableCell
                            colSpan={4}
                            sx={{
                                bgcolor: "action.hover",
                                borderBottom: "1px solid",
                                borderColor: "divider",
                                py: 0.9,
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: { xs: "flex-start", md: "center" },
                                    justifyContent: "space-between",
                                    flexDirection: { xs: "column", md: "row" },
                                    gap: 1.5,
                                }}
                            >
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    {isActiveStage && (
                                        <Chip
                                            label="Biezacy etap"
                                            size="small"
                                            color="success"
                                            variant="outlined"
                                            icon={
                                                <Box
                                                    sx={{
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: "50%",
                                                        bgcolor: "success.main",
                                                        animation: "workflowPulse 1.6s ease-in-out infinite",
                                                        "@keyframes workflowPulse": {
                                                            "0%": { opacity: 0.35 },
                                                            "50%": { opacity: 1 },
                                                            "100%": { opacity: 0.35 },
                                                        },
                                                    }}
                                                />
                                            }
                                            sx={{
                                                mb: 0.4,
                                                fontWeight: 700,
                                                "& .MuiChip-icon": {
                                                    ml: 0.75,
                                                    mr: -0.25,
                                                },
                                            }}
                                        />
                                    )}

                                    <Typography
                                        variant="subtitle2"
                                        sx={{ fontWeight: 700, lineHeight: 1.25 }}
                                    >
                                        {order}. {title}
                                    </Typography>

                                    {description && (
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ display: "block", mt: 0.2, lineHeight: 1.3 }}
                                        >
                                            {description}
                                        </Typography>
                                    )}
                                </Box>

                                <Chip
                                    label={stageTypeLabels[stageType]}
                                    icon={stageTypeChipIcons[stageType]}
                                    size="small"
                                    variant="outlined"
                                    sx={getStageTypeChipSx(stageType, isActiveStage)}
                                />
                            </Box>
                        </TableCell>
                    </TableRow>

                    <TableRow>
                        <TableCell
                            sx={{
                                width: 50,
                                px: 0.5,
                                textAlign: "center",
                                fontWeight: 600,
                            }}
                        >
                            Lp.
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, pl: 1.5 }}>
                            Uzytkownik
                        </TableCell>
                        <TableCell sx={{ width: 360, fontWeight: 600 }}>
                            Status w etapie
                        </TableCell>
                        <TableCell
                            sx={{
                                width: 88,
                                textAlign: "center",
                                fontWeight: 600,
                            }}
                        >
                            Akcje
                        </TableCell>
                    </TableRow>
                </TableHead>

                <TableBody>
                    {members.map((member, index) => {
                        const statusInfo = mapWorkflowActionStatus(
                            member.actionStatus
                        )
                        const showDeadline =
                            isPendingWorkflowActionStatus(member.actionStatus)
                            && Boolean(member.actionDeadline)

                        return (
                            <TableRow key={member.id} hover>
                                <TableCell
                                    sx={{
                                        width: 50,
                                        textAlign: "center",
                                        fontWeight: 600,
                                    }}
                                >
                                    <LpField value={index + 1} />
                                </TableCell>

                                <TableCell sx={{ pl: 1.5 }}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "flex-start",
                                            gap: 1,
                                            minWidth: 0,
                                        }}
                                    >
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <MemberIdentity
                                                name={member.name}
                                                userName={member.userName}
                                                email={member.email}
                                                position={member.position}
                                                companyName={member.companyName}
                                                size="small"
                                            />
                                        </Box>

                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexWrap: "wrap",
                                                justifyContent: "flex-end",
                                                gap: 0.5,
                                                maxWidth: 180,
                                                pt: 0.15,
                                            }}
                                        >
                                            {member.roles.map(role => (
                                                <Chip
                                                    key={role}
                                                    label={role}
                                                    size="small"
                                                    color={roleChipColor[role]}
                                                    variant="outlined"
                                                    sx={{ height: 22 }}
                                                />
                                            ))}
                                        </Box>
                                    </Box>
                                </TableCell>

                                <TableCell>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "flex-start",
                                            gap: 0.45,
                                        }}
                                    >
                                        <Chip
                                            size="small"
                                            color={statusInfo.color}
                                            variant={
                                                statusInfo.waiting
                                                    ? "filled"
                                                    : "outlined"
                                            }
                                            label={statusInfo.label}
                                            sx={{ width: "fit-content" }}
                                        />

                                        {showDeadline && (
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                Termin: {formatDateOnly(
                                                    member.actionDeadline
                                                )}
                                            </Typography>
                                        )}

                                        {member.actionExecDate && (
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                Wykonano: {formatDateOnly(
                                                    member.actionExecDate
                                                )}
                                            </Typography>
                                        )}
                                    </Box>
                                </TableCell>

                                <TableCell sx={{ textAlign: "center" }}>
                                    <Tooltip title="Usun z etapu">
                                        <IconButton
                                            size="small"
                                            onClick={() => onRemoveMemberClick(
                                                stageId,
                                                title,
                                                member
                                            )}
                                            aria-label={`Usun osobe ${member.name} z etapu ${title}`}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        )
                    })}

                    {members.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={4}>
                                <Alert severity="warning" sx={{ my: 0.5 }}>
                                    Brak osob przypisanych do etapu.
                                </Alert>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>

            <Box
                sx={{
                    px: 2,
                    py: 1,
                    borderTop: "1px solid",
                    borderColor: "divider",
                }}
            >
                <Button
                    variant="contained"
                    color={addButtonColor}
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => onAddMemberClick(stageId)}
                >
                    {stageActionLabels[stageType]}
                </Button>
            </Box>
        </TableContainer>
    )
}